import { ApplicationStatusCard } from './ApplicationStatusCard'
import { useApplicationStatusCard } from './useApplicationStatusCard'
import { BackBar } from '../../components/BackBar'
import { Header, HeaderSpacer } from '../../components/Header'
import { WebsiteHeader, WebsiteHeaderSpacer } from '../../components/WebsiteHeader'
import { Text } from '../../components/Text'
import { useThemeColor } from '../../hooks/StatusBarColorPicker'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import { useI18n } from '../../i18n'

/**
 * Application Status screen — a drill-in page reached from Home (the home-side
 * navigation isn't wired yet; this just establishes the screen + route).
 *
 * Layout, top-down:
 *   1. Status-aware fixed header — website header for prospect/applicant,
 *      app header for participant.
 *   2. <BackBar> — reusable "go back" row showing this page's title.
 *   3. Content — the status sections get added below as they're built.
 *
 * This is a standalone shell (its own Header, no bottom tab bar) because it's a
 * back-navigable detail page, not a primary tab. It mirrors AppShell's header +
 * safe-area scaffolding without the TabBar.
 */
export function ApplicationStatusScreen() {
  const user = useCurrentUser()
  const cardProps = useApplicationStatusCard()
  const isParticipant = user.status === 'participant'
  useThemeColor(isParticipant ? '#454548' : '#FFFFFF')
  const { t } = useI18n()

  return (
    <div className="min-h-dvh bg-gray-50">
      {isParticipant ? <Header /> : <WebsiteHeader />}
      <main className="min-h-dvh">
        {isParticipant ? <HeaderSpacer /> : <WebsiteHeaderSpacer />}
        <div className="mx-auto w-full max-w-screen-sm md:max-w-4xl">
          <BackBar label={t('applicationStatus.title')} />

          {/* Section heading. Padding 8/24/8/24 → py-2 px-6, aligned with the
              back bar's left edge. Sits on the gray-50 page background. */}
          <Text as="h2" variant="h3" weight="bold" className="px-6 py-2 text-gray-700">
            {t('applicationStatus.overview')}
          </Text>

          {/* Step choreography is still sample data; assetId + amount are live. */}
          <div className="px-4">
            <ApplicationStatusCard {...cardProps} />
          </div>

          {/* Status sections are added here, top-down, as they're built. */}
        </div>
      </main>
    </div>
  )
}
